import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "design-system/atoms/Dialog"
import { Plus } from "lucide-react"
import { useStartTestingConversationMutation } from "modules/conversations/hooks/useStartTestingConversationMutation"
import { useTestingExperimentOptionsQuery } from "modules/conversations/hooks/useTestingExperimentOptionsQuery"
import { useDeveloperMode } from "modules/shared/hooks/useDeveloperMode"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { ConversationResponseDataSchema, CustomerResponseDataSchema, DataResponse } from "schema"
import { toast } from "sonner"
import z from "zod"
import { TestingExperiment, TestingExperimentSelect } from "./TestingExperimentSelect"

type Props = {
  customer: z.infer<typeof CustomerResponseDataSchema>
  campaignId?: string
  onTestingConversationStarted: (
    response: DataResponse<z.infer<typeof ConversationResponseDataSchema>>,
  ) => unknown
}

export function CreateFakeConversationDialog({
  customer,
  campaignId,
  onTestingConversationStarted,
}: Props) {
  const [open, setOpen] = useState(false)
  const [experiment, setExperiment] = useState<TestingExperiment | null>(null)
  const { developerMode } = useDeveloperMode()
  const {
    data: experimentOptions = [],
    isLoading: isOptionsLoading,
    isError: isOptionsError,
  } = useTestingExperimentOptionsQuery(developerMode && !!campaignId && customer.isFake)
  const isExperimentLoading = developerMode && isOptionsLoading

  const { t } = useTranslation()
  const { mutateAsync: startTestingConversation, isPending: isStartingTestingConversation } =
    useStartTestingConversationMutation({
      onSuccess: onTestingConversationStarted,
    })

  async function onStartTestingConversation() {
    await startTestingConversation({
      customerId: customer.id,
      campaignId: campaignId ?? "",
      experiment: developerMode ? experiment : undefined,
    })
    setOpen(false)
    toast.success(t("conversations.chat.noConversations.testingConversationCreated"))
  }

  if (!customer.isFake) return null
  if (!campaignId) return null

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen)
        setExperiment(null)
      }}
    >
      <DialogTrigger asChild>
        <Button
          variant="testing"
          disabled={isStartingTestingConversation}
          className="w-fit"
          size={"sm"}
        >
          <Plus className="size-4" />
          <span>{t("conversations.chat.noConversations.createTestingConversation")}</span>
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t("conversations.chat.noConversations.createTestingConversation")}
          </DialogTitle>
          <DialogDescription>
            {t("conversations.chat.noConversations.createTestingConversationDescription")}
          </DialogDescription>
        </DialogHeader>
        {developerMode && (
          <TestingExperimentSelect
            options={experimentOptions}
            value={experiment}
            onChange={setExperiment}
            disabled={isStartingTestingConversation}
          />
        )}
        {developerMode && isOptionsError && (
          <span className="text-sm text-destructive">{t("shared.error.description")}</span>
        )}
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={isStartingTestingConversation}>
              <span>{t("shared.cancel")}</span>
            </Button>
          </DialogClose>
          <Button
            variant="testing"
            onClick={onStartTestingConversation}
            disabled={
              isStartingTestingConversation ||
              isExperimentLoading ||
              (developerMode && isOptionsError)
            }
          >
            <span>{t("shared.confirm")}</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
